import { useEffect, useMemo, useState } from 'react'
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  Clock3,
  EyeOff,
  Inbox,
  Lightbulb,
  Loader2,
  LogIn,
  LogOut,
  MessageSquareHeart,
  ShieldCheck,
  ThumbsDown,
  ThumbsUp,
  TriangleAlert,
  UserRoundCheck,
} from 'lucide-react'
import {
  AdminVoiceItem,
  clearSession,
  exchangeSso,
  getCurrentUser,
  getSsoLoginUrl,
  getStoredUser,
  getToken,
  loadAdmin,
  loadFeed,
  logout,
  refreshSession,
  SessionUser,
  updateAdmin,
  VoiceItem,
  VoiceStats,
  vote,
} from './api'

type View = 'feed' | 'privacy' | 'admin'

const categories: Record<string, string> = {
  suggestion: 'Đề xuất',
  policy_process: 'Chính sách & quy trình',
  workplace_concern: 'Môi trường làm việc',
  compensation_benefits: 'Lương thưởng & quyền lợi',
  interpersonal_conflict: 'Quan hệ công việc',
  harassment_discrimination: 'Ứng xử không phù hợp',
  safety_security: 'An toàn & bảo mật',
  other: 'Khác',
}

const workflow: Record<string, { label: string; className: string }> = {
  received: { label: 'Đã tiếp nhận', className: 'status-received' },
  in_progress: { label: 'Đang xử lý', className: 'status-progress' },
  resolved: { label: 'Đã xử lý', className: 'status-resolved' },
}

export function App() {
  const [user, setUser] = useState<SessionUser | null>(() => getStoredUser())
  const [checking, setChecking] = useState(true)
  const [authError, setAuthError] = useState('')

  useEffect(() => {
    let active = true
    async function bootstrap() {
      if (window.location.pathname === '/auth/sso/callback') {
        const params = new URLSearchParams(window.location.search)
        const code = params.get('code') || params.get('ssoToken')
        if (!code) {
          setAuthError('Thiếu mã xác thực SSO. Vui lòng đăng nhập lại.')
          setChecking(false)
          return
        }
        try {
          const nextUser = await exchangeSso(code)
          if (active) {
            setUser(nextUser)
            window.history.replaceState({}, '', '/')
          }
        } catch (error) {
          if (active) setAuthError(messageOf(error))
        } finally {
          if (active) setChecking(false)
        }
        return
      }
      if (getToken()) {
        try {
          const nextUser = await getCurrentUser()
          if (active) {
            setUser(nextUser)
            setChecking(false)
          }
          return
        } catch {
          // Access token may have expired; the refresh cookie can still recover it.
        }
      }
      const nextUser = await refreshSession().catch(() => null)
      if (active) {
        setUser(nextUser)
        setChecking(false)
      }
    }
    void bootstrap()
    return () => { active = false }
  }, [])

  if (checking) return <CenteredState icon={<Loader2 className="spin" />} title="Đang kiểm tra phiên SSO" text="Vui lòng đợi trong giây lát." />
  if (!user) return <Login error={authError} />
  return <Dashboard user={user} onExpired={() => { clearSession(); setUser(null) }} />
}

function Login({ error }: { error: string }) {
  return (
    <main className="login-shell" id="main-content">
      <section className="login-card" aria-labelledby="login-title">
        <div className="brand-mark"><MessageSquareHeart aria-hidden="true" /></div>
        <p className="eyebrow">Kênh nội bộ GearVN</p>
        <h1 id="login-title">Có điều khó nói, vẫn có nơi để nói.</h1>
        <p className="lead">Gửi qua agent Lark. Dashboard chỉ hiển thị bản đã khử nhận diện và dùng SSO để bảo vệ quyền xem, chống vote trùng.</p>
        <div className="trust-list">
          <span><EyeOff aria-hidden="true" /> Không hiện tên người gửi</span>
          <span><ShieldCheck aria-hidden="true" /> Nội dung nhạy cảm phải duyệt</span>
          <span><UserRoundCheck aria-hidden="true" /> Một lựa chọn mỗi tài khoản</span>
        </div>
        {error && <div className="alert" role="alert"><TriangleAlert aria-hidden="true" />{error}</div>}
        <a className="button primary full" href={getSsoLoginUrl()}>
          <LogIn aria-hidden="true" /> Đăng nhập SSO GearVN <ArrowRight aria-hidden="true" />
        </a>
        <p className="microcopy">SSO không được hiển thị cùng nội dung đề xuất. Hệ thống vẫn có log bảo mật phục vụ vận hành.</p>
      </section>
    </main>
  )
}

function Dashboard({ user, onExpired }: { user: SessionUser; onExpired: () => void }) {
  const [view, setView] = useState<View>(() => user.isAdmin ? 'admin' : 'feed')
  const [items, setItems] = useState<VoiceItem[]>([])
  const [stats, setStats] = useState<VoiceStats>({ published: 0, pending: 0, inProgress: 0, resolved: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [department, setDepartment] = useState('all')

  async function reload() {
    setLoading(true)
    setError('')
    try {
      const data = await loadFeed()
      setItems(Array.isArray(data.items) ? data.items : [])
      setStats(data.stats ?? { published: 0, pending: 0, inProgress: 0, resolved: 0 })
    } catch (err) {
      const message = messageOf(err)
      if (message.includes('hết hạn')) onExpired()
      else setError(message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void reload() }, [])

  const departments = useMemo(() => [...new Set(items.map((item) => item.departmentCode).filter(Boolean) as string[])].sort(), [items])
  const visibleItems = department === 'all' ? items : items.filter((item) => item.departmentCode === department)

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand-button" onClick={() => setView('feed')} aria-label="Về bảng đề xuất">
          <span className="brand-mark small"><MessageSquareHeart aria-hidden="true" /></span>
          <span><strong>Lắng nghe nội bộ</strong><small>Góp ý an toàn · Hành động minh bạch</small></span>
        </button>
        <div className="user-area">
          {user.isAdmin && <span className="admin-badge"><ShieldCheck aria-hidden="true" />Quản trị</span>}
          <span className="user-name">{user.name}</span>
          <button className="icon-button" onClick={() => void logout()} aria-label="Đăng xuất"><LogOut aria-hidden="true" /></button>
        </div>
      </header>

      <div className="layout">
        <aside className="sidebar" aria-label="Điều hướng chính">
          <NavButton active={view === 'feed'} icon={<Lightbulb />} label="Bảng đề xuất" onClick={() => setView('feed')} />
          <NavButton active={view === 'privacy'} icon={<ShieldCheck />} label="Quyền riêng tư" onClick={() => setView('privacy')} />
          {user.isAdmin && <NavButton active={view === 'admin'} icon={<Inbox />} label="Hàng đợi duyệt" onClick={() => setView('admin')} badge={stats.pending} />}
          <div className="sidebar-note">
            <EyeOff aria-hidden="true" />
            <p><strong>Không hiện tên người gửi</strong><br />Bản public đã được khử nhận diện.</p>
          </div>
        </aside>

        <main id="main-content" className="content" tabIndex={-1}>
          {view === 'feed' && (
            <FeedView
              items={visibleItems}
              stats={stats}
              loading={loading}
              error={error}
              departments={departments}
              department={department}
              setDepartment={setDepartment}
              onReload={reload}
              onVote={async (id, value) => {
                const result = await vote(id, value)
                setItems((current) => current.map((item) => item.publicId === id ? { ...item, viewerVote: result.vote, likeCount: result.likeCount, dislikeCount: result.dislikeCount } : item))
              }}
            />
          )}
          {view === 'privacy' && <PrivacyView />}
          {view === 'admin' && user.isAdmin && <AdminView onStatsChanged={reload} />}
        </main>
      </div>

      <nav className="bottom-nav" aria-label="Điều hướng trên điện thoại">
        <NavButton active={view === 'feed'} icon={<Lightbulb />} label="Đề xuất" onClick={() => setView('feed')} />
        <NavButton active={view === 'privacy'} icon={<ShieldCheck />} label="Riêng tư" onClick={() => setView('privacy')} />
        {user.isAdmin && <NavButton active={view === 'admin'} icon={<Inbox />} label="Duyệt" onClick={() => setView('admin')} badge={stats.pending} />}
      </nav>
    </div>
  )
}

function FeedView(props: {
  items: VoiceItem[]
  stats: VoiceStats
  loading: boolean
  error: string
  departments: string[]
  department: string
  setDepartment: (value: string) => void
  onReload: () => Promise<void>
  onVote: (id: string, value: 1 | -1) => Promise<void>
}) {
  return (
    <>
      <section className="hero-panel">
        <div>
          <p className="eyebrow">Tiếng nói được lắng nghe</p>
          <h1>Ý kiến rõ ràng. Danh tính được bảo vệ.</h1>
          <p>Đề xuất công khai chỉ là bản đã khử nhận diện. Nội dung nhạy cảm được giữ riêng cho đến khi quản trị duyệt.</p>
        </div>
        <div className="lark-card">
          <MessageSquareHeart aria-hidden="true" />
          <div><strong>Muốn gửi nội dung mới?</strong><span>Nhắn trực tiếp agent Lark “Lắng nghe nội bộ”.</span></div>
          <ChevronRight aria-hidden="true" />
        </div>
      </section>

      <section className="stats-grid" aria-label="Tổng quan tiến độ">
        <Stat icon={<Lightbulb />} value={props.stats.published} label="Đề xuất công khai" />
        <Stat icon={<Clock3 />} value={props.stats.inProgress} label="Đang xử lý" />
        <Stat icon={<CheckCircle2 />} value={props.stats.resolved} label="Đã xử lý" />
      </section>

      <section className="feed-toolbar">
        <div><h2>Bảng đề xuất chung</h2><p>Mỗi tài khoản có một lựa chọn đang hiệu lực cho mỗi đề xuất.</p></div>
        <label className="select-label">Bộ phận
          <select value={props.department} onChange={(event) => props.setDepartment(event.target.value)}>
            <option value="all">Tất cả bộ phận</option>
            {props.departments.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
      </section>

      {props.error && <div className="alert" role="alert"><TriangleAlert />{props.error}<button onClick={() => void props.onReload()}>Thử lại</button></div>}
      {props.loading ? <div className="loading-list"><Loader2 className="spin" /> Đang tải đề xuất…</div> : (
        <div className="voice-list">
          {props.items.map((item) => <VoiceCard key={item.publicId} item={item} onVote={props.onVote} />)}
          {!props.items.length && <EmptyState />}
        </div>
      )}
    </>
  )
}

function VoiceCard({ item, onVote }: { item: VoiceItem; onVote: (id: string, value: 1 | -1) => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const status = workflow[item.workflowStatus] ?? workflow.received
  async function choose(value: 1 | -1) {
    if (busy || item.viewerVote === value) return
    setBusy(true); setError('')
    try { await onVote(item.publicId, value) }
    catch (err) { setError(messageOf(err)) }
    finally { setBusy(false) }
  }
  return (
    <article className="voice-card">
      <div className="card-topline">
        <span className="category-pill">{categories[item.category] ?? 'Góp ý'}</span>
        <span className={`status-pill ${status.className}`}><CircleDot aria-hidden="true" />{status.label}</span>
      </div>
      <h3>{item.title}</h3>
      <p>{item.body}</p>
      <div className="card-meta">
        <span>{item.departmentCode || 'Liên bộ phận'}</span>
        <span aria-label={`Mã đề xuất ${item.publicId}`}>{item.publicId}</span>
        <time dateTime={item.publishedAt}>{formatDate(item.publishedAt)}</time>
      </div>
      <div className="vote-row" aria-label="Đánh giá đề xuất">
        <button className={item.viewerVote === 1 ? 'vote active-like' : 'vote'} onClick={() => void choose(1)} disabled={busy} aria-pressed={item.viewerVote === 1}>
          <ThumbsUp aria-hidden="true" /> Hữu ích <strong>{item.likeCount}</strong>
        </button>
        <button className={item.viewerVote === -1 ? 'vote active-dislike' : 'vote'} onClick={() => void choose(-1)} disabled={busy} aria-pressed={item.viewerVote === -1}>
          <ThumbsDown aria-hidden="true" /> Chưa đồng ý <strong>{item.dislikeCount}</strong>
        </button>
        {busy && <Loader2 className="spin inline-loader" aria-label="Đang lưu lựa chọn" />}
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
    </article>
  )
}

function PrivacyView() {
  return (
    <section className="reading-page">
      <p className="eyebrow">Quyền riêng tư</p>
      <h1>Ẩn tên trên dashboard, minh bạch về giới hạn.</h1>
      <p className="lead">Hệ thống được thiết kế để giảm rủi ro lộ người gửi, không đưa ra lời hứa “ẩn danh tuyệt đối”.</p>
      <div className="principle-grid">
        <Principle icon={<EyeOff />} title="Không hiện tên" text="Feed chung không có tên, email, ID Lark hay thông tin liên hệ của người gửi." />
        <Principle icon={<ShieldCheck />} title="Duyệt trước khi công khai" text="Lương thưởng, chỉ trích cá nhân, công kích, cáo buộc và vấn đề an toàn luôn vào hàng đợi riêng." />
        <Principle icon={<UserRoundCheck />} title="SSO chống vote trùng" text="Danh tính SSO được băm tại lớp máy chủ để bảo đảm một lựa chọn đang hiệu lực cho mỗi đề xuất." />
      </div>
      <div className="notice-panel">
        <TriangleAlert aria-hidden="true" />
        <div><strong>Điều cần biết trước khi gửi</strong><p>GoClaw/Lark vẫn có log vận hành bảo mật. Đừng gửi mật khẩu, token, dữ liệu khách hàng hoặc bí mật không cần thiết. Nếu có nguy hiểm tức thời, hãy dùng kênh khẩn cấp phù hợp thay vì chỉ chờ dashboard.</p></div>
      </div>
      <h2>Luồng xử lý</h2>
      <ol className="steps">
        <li><span>1</span><div><strong>Nhắn agent Lark</strong><p>Chia sẻ theo cách bạn thấy thoải mái; bộ phận là tùy chọn.</p></div></li>
        <li><span>2</span><div><strong>Phân loại & khử nhận diện</strong><p>Agent tạo bản public-safe; server kiểm tra nhạy cảm thêm một lớp.</p></div></li>
        <li><span>3</span><div><strong>Duyệt hoặc công khai</strong><p>Nội dung an toàn có thể lên feed; nội dung nhạy cảm chờ quản trị.</p></div></li>
        <li><span>4</span><div><strong>Cập nhật tiến độ</strong><p>Quản trị chuyển trạng thái: đã tiếp nhận, đang xử lý, đã xử lý.</p></div></li>
      </ol>
    </section>
  )
}

function AdminView({ onStatsChanged }: { onStatsChanged: () => Promise<void> }) {
  const [items, setItems] = useState<AdminVoiceItem[]>([])
  const [selected, setSelected] = useState<AdminVoiceItem | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  async function reload() {
    setLoading(true); setError('')
    try {
      const data = await loadAdmin()
      setItems(Array.isArray(data.items) ? data.items : [])
      if (selected) setSelected(data.items.find((item) => item.publicId === selected.publicId) ?? null)
    } catch (err) { setError(messageOf(err)) }
    finally { setLoading(false) }
  }
  useEffect(() => { void reload() }, [])
  return (
    <section>
      <div className="admin-heading"><div><p className="eyebrow">Chỉ quản trị</p><h1>Hàng đợi kiểm duyệt</h1><p>Ưu tiên rủi ro cao. Không copy nội dung gốc sang kênh công khai.</p></div><span className="confidential-badge"><ShieldCheck />Nội dung mật</span></div>
      {error && <div className="alert" role="alert"><TriangleAlert />{error}</div>}
      {loading ? <div className="loading-list"><Loader2 className="spin" /> Đang tải hàng đợi…</div> : (
        <div className="admin-grid">
          <div className="queue-list">
            {items.map((item) => (
              <button key={item.publicId} className={selected?.publicId === item.publicId ? 'queue-item selected' : 'queue-item'} onClick={() => setSelected(item)}>
                <span className={`risk-dot risk-${item.riskLevel}`} aria-label={`Rủi ro ${item.riskLevel}`} />
                <span><strong>{item.title || 'Chưa có bản public'}</strong><small>{item.publicId} · {categories[item.category]}</small></span>
                <ChevronRight aria-hidden="true" />
              </button>
            ))}
            {!items.length && <EmptyState />}
          </div>
          <div className="review-panel">
            {selected ? <ReviewForm item={selected} onSaved={async () => { await reload(); await onStatsChanged() }} /> : <div className="review-placeholder"><Inbox /><h2>Chọn một nội dung để duyệt</h2><p>Nội dung gốc chỉ hiện ở khu vực quản trị này.</p></div>}
          </div>
        </div>
      )}
    </section>
  )
}

function ReviewForm({ item, onSaved }: { item: AdminVoiceItem; onSaved: () => Promise<void> }) {
  const [title, setTitle] = useState(item.title)
  const [body, setBody] = useState(item.body)
  const [note, setNote] = useState(item.adminNote)
  const [status, setStatus] = useState(item.workflowStatus)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  useEffect(() => { setTitle(item.title); setBody(item.body); setNote(item.adminNote); setStatus(item.workflowStatus); setError(''); setSuccess('') }, [item.publicId])
  async function save(action = '') {
    setBusy(true); setError(''); setSuccess('')
    try {
      await updateAdmin(item.publicId, { action, publicTitle: title, publicBody: body, adminNote: note, workflowStatus: status })
      setSuccess(action === 'approve' ? 'Đã duyệt và công khai bản ẩn danh.' : 'Đã cập nhật.')
      await onSaved()
    } catch (err) { setError(messageOf(err)) }
    finally { setBusy(false) }
  }
  return (
    <form onSubmit={(event) => { event.preventDefault(); void save() }}>
      <div className="review-title"><div><span className={`risk-label risk-${item.riskLevel}`}>Rủi ro {item.riskLevel}</span><h2>{item.publicId}</h2></div><span>{item.moderationState}</span></div>
      <div className="sensitive-tags">{item.sensitivityLabels.map((label) => <span key={label}>{label}</span>)}</div>
      <label>Nội dung gốc — không công khai<textarea className="raw-text" value={item.rawText} readOnly rows={7} /></label>
      <label>Tiêu đề public<input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={180} /></label>
      <label>Bản public đã khử nhận diện<textarea value={body} onChange={(event) => setBody(event.target.value)} rows={5} maxLength={6000} /></label>
      <div className="form-row">
        <label>Trạng thái<select value={status} onChange={(event) => setStatus(event.target.value as VoiceItem['workflowStatus'])}><option value="received">Đã tiếp nhận</option><option value="in_progress">Đang xử lý</option><option value="resolved">Đã xử lý</option></select></label>
        <label>Ghi chú quản trị<input value={note} onChange={(event) => setNote(event.target.value)} maxLength={4000} /></label>
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
      {success && <p className="field-success" role="status">{success}</p>}
      <div className="action-bar">
        <button type="button" className="button primary" disabled={busy} onClick={() => void save('approve')}><CheckCircle2 />Duyệt & công khai</button>
        <button type="button" className="button secondary" disabled={busy} onClick={() => void save('redaction_required')}>Yêu cầu khử nhận diện lại</button>
        <button type="button" className="button ghost" disabled={busy} onClick={() => void save('private_case')}>Giữ hồ sơ riêng</button>
        <button type="submit" className="button ghost" disabled={busy}>Lưu trạng thái</button>
      </div>
    </form>
  )
}

function NavButton({ active, icon, label, onClick, badge }: { active: boolean; icon: React.ReactNode; label: string; onClick: () => void; badge?: number }) {
  return <button className={active ? 'nav-button active' : 'nav-button'} onClick={onClick} aria-current={active ? 'page' : undefined}>{icon}<span>{label}</span>{badge ? <b>{badge}</b> : null}</button>
}

function Stat({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return <div className="stat-card"><span>{icon}</span><div><strong>{value}</strong><small>{label}</small></div></div>
}

function Principle({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return <article className="principle"><span>{icon}</span><h2>{title}</h2><p>{text}</p></article>
}

function EmptyState() {
  return <div className="empty-state"><Lightbulb /><h3>Chưa có nội dung phù hợp</h3><p>Đề xuất sẽ xuất hiện sau khi được khử nhận diện và duyệt an toàn.</p></div>
}

function CenteredState({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return <main className="centered-state" id="main-content">{icon}<h1>{title}</h1><p>{text}</p></main>
}

function formatDate(value: string): string {
  try { return new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value)) }
  catch { return value }
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Đã có lỗi. Vui lòng thử lại.'
}
